import { GenerationResponseVO } from '../../../manager/generation-response.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AlertButton } from '@shared/alert/alertButton';
import { StringUtils } from '@shared/util/string-utils';
import { FileUtils } from '@shared/util/file-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { first, mergeMap } from 'rxjs/operators';
import { of, timer } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Injectable } from '@angular/core';
import { BackgroundTasksInfoService } from '@project/service/background-tasks-info.service';
import { CaeDataService } from '../../../cae/cae-data.service';

@Injectable({ providedIn: 'root' })
export class CaeProjectExportService {

  constructor(
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private backgroundTasksInfoService: BackgroundTasksInfoService,
    private caeDataService: CaeDataService,
  ) {}

  public exportProjectGroupToCae(data: { selectedId: number, selectedName: string }): void {
    this.backgroundTasksInfoService.setTaskActive("cae-export", true);
    this.checkCaeGenerationResult(this.caeDataService.exportProjectGroupToCae(data), data);
  }

  public exportProjectToCae(data: { selectedId: number, selectedName: string }): void {
    this.backgroundTasksInfoService.setTaskActive("cae-export", true);
    this.checkCaeGenerationResult(this.caeDataService.exportProjectToCae(data), data);
  }

  private checkCaeGenerationResult(request: Observable<GenerationResponseVO>, data: { selectedId: number, selectedName: string }) {
    request.pipe(
      mergeMap(response => {
        if (response.contentGenerated) {
          // If first request has generated the response use it.
          return of(response);
        } else {
          // If there is no generated response check every 5 seconds.
          return timer(5000, 5000).pipe(
            mergeMap(() => this.caeDataService.checkCaeGenerationResult(response.contentId)),
            first(response => response.contentGenerated));
        }
      })
    ).subscribe({
      next: ((generationResponse: GenerationResponseVO) => this.handleCaeExportResponse(generationResponse, data)),
      error: ((error) => this.caeExportFailure(error))
    });
  }

  private handleCaeExportResponse(generationResponseVO: GenerationResponseVO, data: { selectedId: number, selectedName: string }): void {
    this.caeExportFinished({ response: generationResponseVO, triggerMessage: data });
  }

  private caeExportFinished(data: { response: GenerationResponseVO, triggerMessage: any }): void {
    this.backgroundTasksInfoService.setTaskActive("cae-export", false);
    const response: GenerationResponseVO = data.response;
    const triggerMessage: any = data.triggerMessage;
    if (!response) {
      return;
    }
    this.alertHelper.showAlert(
      'main.title.export_to_cae_finished',
      'main.message.export_to_cae_finished',
      undefined,
      [AlertButton.OK, AlertButton.CANCEL],
      "info",
      null,
      true,
      false
      ).then((answer) => {
      if (answer === AlertButton.OK) {
        try {
          const fileName: string = StringUtils.sanitizeFileName(triggerMessage.selectedName);
          FileUtils.saveTextFile(fileName, response.content, '.xml', true);
        } catch (error: unknown) {
          if (!(error instanceof Error)) throw error;
          this.onFileSaveIOError(error);
        }
      }
    });
  }

  private caeExportFailure(error: Error): void {
    this.backgroundTasksInfoService.setTaskActive("cae-export", false);
    const faultString: string = error.message;
    const title: string = this.localeResourceManager.getValueByKey('main.title.error');
    if (faultString.lastIndexOf('cae_group_export_error') > -1) {
      this.alertHelper.showError(title, "projectNavigation.message.project_group_export_impossible");
    } else if (faultString.lastIndexOf('cae_group_child_export_error') > -1) {
      this.alertHelper.showError(title, "projectNavigation.message.cae_project_group_count");
    } else if (faultString.lastIndexOf('cae_project_export_error') > -1) {
      this.alertHelper.showError(title, "projectNavigation.message.project_export_impossible");
    } else {
      this.alertHelper.showError(title, "projectNavigation.message.project_export_impossible", {translationParams: [faultString]});
    }
  }

  private onFileSaveIOError(error: Error): void {
    const title: string = this.localeResourceManager.getValueByKey('main.title.error');
    const message: string = this.localeResourceManager.getValueByKey('main.message.save_cae_file_error', [error.message]);
    this.alertHelper.showError(title, message);
  }
}
